import type { AuthenticationResult, SilentRequest } from "@azure/msal-browser";
import { storeToRefs } from "pinia";
import { useAuthStore } from "@/store/authStore";
import { useIcs4Store } from "@/store/storeIcs4";
import { selectProviderAccount } from "./selectProviderAccount";
import { setUserAccount, userAzureId, userOid } from "./sessionState";
import type { MsalFlowDeps } from "@ics/auth-api";

const debugPrefix = '[Auth]';

// *************************************************
// WARM-RESTORE (silent-only — for `EntraProvider.restore()` ved app-boot)
//
// `checkAuthentication()` (popupFlow) inkluderer popup-fallback når silent
// feiler. Det er greit for interaktive flows (Login-knapp), men ødeleggende
// under `main.js`-bootstrap der ingen user gesture finnes: popup blokkeres
// taust av nettleseren, MSAL venter ut sin `popupBridgeTimeout` (60s default),
// og `app.mount('#app')` blir aldri kalt → blank skjerm.
//
// `silentRestoreEntra()` er en ren silent-sti med Promise.race-timeout som
// garantert resolver innen RESTORE_TIMEOUT_MS, slik at `warmRestoreSession()`
// alltid returnerer raskt nok til at router-guarden kan ruse videre.

export const RESTORE_TIMEOUT_MS = 5000;

export async function silentRestoreEntra (deps: MsalFlowDeps): Promise<boolean> {
  try {
    const instance = await deps.getInstance();
    // WI 2291: MSAL deler konto-registeret på tvers av providere på samme origin.
    // Velg Entra-tenant-kontoen — ikke `accounts[0]`, som kan være External
    // ID-providerens konto og gi ikke-deterministisk warm-restore.
    const authority = await deps.getAuthority();
    const account = selectProviderAccount(
      instance.getAllAccounts(),
      { authority },
      instance.getActiveAccount()
    );
    if (!account) {
      return false;
    }

    instance.setActiveAccount(account);
    userAzureId.value = account.username;
    userOid.value = deps.resolveOid(account);
    setUserAccount(account);

    const tokenConfig = await deps.getTokenRequest();

    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    const timeoutPromise = new Promise<null>((resolve) => {
      timeoutHandle = setTimeout(() => resolve(null), RESTORE_TIMEOUT_MS);
    });

    const silentPromise: Promise<AuthenticationResult | null> = instance
      .acquireTokenSilent({
        account,
        ...tokenConfig,
        prompt: "none"
      } as SilentRequest)
      .catch((error: unknown) => {
        console.debug(`${debugPrefix} silentRestoreEntra acquireTokenSilent feilet:`, error);
        return null;
      });

    const result = await Promise.race([silentPromise, timeoutPromise]);
    if (timeoutHandle !== null) {
      clearTimeout(timeoutHandle);
    }

    if (!result || !result.accessToken) {
      setUserAccount(null);
      return false;
    }

    const authStore = useAuthStore();
    authStore.setAuthStatus(true, account.name ?? null);
    const ics4Store = useIcs4Store();
    const { isAuth, userName } = storeToRefs(ics4Store);
    isAuth.value = true;
    if (account.name) {
      userName.value = account.name;
    }
    return true;
  } catch (error) {
    console.debug(`${debugPrefix} silentRestoreEntra feilet:`, error);
    setUserAccount(null);
    return false;
  }
}
